import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as uptimeApi from '../api/uptime';
import * as sitesApi from '../api/sites';
import * as exportsApi from '../api/exports';

export function Uptime() {
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [incidents, setIncidents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sites, setSites] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    from: '',
    to: '',
    programId: ''
  });
  const [filters, setFilters] = useState({
    siteId: '',
    assetId: '',
    from: '',
    to: ''
  });

  const loadSites = useCallback(async () => {
    if (!selectedOrg) return;
    try {
      const sitesList = await sitesApi.getSites(selectedOrg.id);
      setSites(sitesList);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  }, [selectedOrg]);

  const loadTimeline = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [timelineResult, summaryResult] = await Promise.all([
        uptimeApi.getUptimeTimeline(selectedOrg.id, {
          siteId: filters.siteId || undefined,
          assetId: filters.assetId || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined
        }),
        uptimeApi.getUptimeSummary(selectedOrg.id, {
          siteId: filters.siteId || undefined,
          assetId: filters.assetId || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined
        })
      ]);
      setIncidents(timelineResult.incidents);
      setLastUpdated(timelineResult.lastUpdated);
      setSummary(summaryResult);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, filters]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleIncidentClick = (incidentId) => {
    navigate(`/uptime/incidents/${incidentId}`);
  };

  const handleOpenExportModal = () => {
    setExportFilters({
      from: filters.from || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      to: filters.to || new Date().toISOString().split('T')[0],
      programId: ''
    });
    setShowExportModal(true);
  };

  const handleExport = async () => {
    if (!selectedOrg || exporting) return;
    if (!exportFilters.from || !exportFilters.to) {
      setError('Please select a date range for export');
      return;
    }
    setExporting(true);
    try {
      const exportJob = await exportsApi.createUptimeExport(selectedOrg.id, {
        from: exportFilters.from,
        to: exportFilters.to,
        programId: exportFilters.programId || undefined,
        siteId: filters.siteId || undefined
      });
      if (exportJob.status === 'completed' && exportJob.artifactId) {
        const { blob, filename } = await exportsApi.downloadArtifact(selectedOrg.id, exportJob.artifactId);
        exportsApi.triggerDownload(blob, filename);
      }
      setShowExportModal(false);
    } catch (err) {
      setError('Failed to export uptime report');
    } finally {
      setExporting(false);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view uptime data.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader 
        title="Uptime"
        subtitle={`Incident timeline for ${selectedOrg?.name}`}
      />

      {summary && (
        <div style={styles.summaryCards}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{summary.uptimePercent}%</div>
            <div style={styles.summaryLabel}>Uptime</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{summary.totalIncidents}</div>
            <div style={styles.summaryLabel}>Incidents</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{summary.totalDowntimeMinutes}</div>
            <div style={styles.summaryLabel}>Downtime (min)</div>
          </div>
        </div>
      )}

      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <select
            value={filters.siteId}
            onChange={(e) => handleFilterChange('siteId', e.target.value)}
            style={styles.select}
            data-testid="uptime-site-filter"
          >
            <option value="">All Sites</option>
            {sites.map(site => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Asset ID"
            value={filters.assetId}
            onChange={(e) => handleFilterChange('assetId', e.target.value)}
            style={styles.input}
            data-testid="uptime-asset-filter"
          />
          <input
            type="date"
            value={filters.from}
            onChange={(e) => handleFilterChange('from', e.target.value)}
            style={styles.input}
            data-testid="uptime-from-filter"
          />
          <input
            type="date"
            value={filters.to}
            onChange={(e) => handleFilterChange('to', e.target.value)}
            style={styles.input}
            data-testid="uptime-to-filter"
          />
        </div>
        <div style={styles.toolbarActions}>
          <button
            onClick={handleOpenExportModal}
            style={styles.exportButton}
            data-testid="export-uptime-button"
          >
            Export CSV
          </button>
        </div>
        {lastUpdated && (
          <span style={styles.lastUpdated}>
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </span>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading timeline...</div>
      ) : (
        <div style={styles.timeline} data-testid="uptime-timeline">
          {incidents.length === 0 ? (
            <div style={styles.emptyState}>No incidents found</div>
          ) : (
            incidents.map(incident => (
              <div 
                key={incident.id} 
                style={styles.timelineItem}
                onClick={() => handleIncidentClick(incident.id)}
                data-testid={`incident-${incident.id}`}
              >
                <div style={styles.timelineMarker}>
                  <div style={{
                    ...styles.markerDot,
                    background: getStatusColor(incident.status)
                  }} />
                  <div style={styles.markerLine} />
                </div>
                <div style={styles.timelineContent}>
                  <div style={styles.incidentHeader}>
                    <span style={styles.incidentType}>{incident.type}</span>
                    <span style={{
                      ...styles.statusBadge,
                      background: getStatusColor(incident.status)
                    }}>
                      {incident.status}
                    </span>
                  </div>
                  <div style={styles.incidentMeta}>
                    <span>{new Date(incident.startTime).toLocaleString()}</span>
                    {incident.endTime && (
                      <span> — {new Date(incident.endTime).toLocaleString()}</span>
                    )}
                  </div>
                  <div style={styles.incidentDetails}>
                    <span>Duration: {incident.durationMinutes || 'Ongoing'} min</span>
                    <span>Asset: {incident.assetId}</span>
                  </div>
                  {incident.description && (
                    <div style={styles.incidentDescription}>{incident.description}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showExportModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="export-modal">
            <h3 style={styles.modalTitle}>Export Uptime Report</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>From Date</label>
              <input
                type="date"
                value={exportFilters.from}
                onChange={(e) => setExportFilters(prev => ({ ...prev, from: e.target.value }))}
                style={styles.modalInput}
                data-testid="export-from-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>To Date</label>
              <input
                type="date"
                value={exportFilters.to}
                onChange={(e) => setExportFilters(prev => ({ ...prev, to: e.target.value }))}
                style={styles.modalInput}
                data-testid="export-to-input"
              />
            </div>
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowExportModal(false)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={exporting}
                style={styles.submitButton}
                data-testid="confirm-export-button"
              >
                {exporting ? 'Exporting...' : 'Export'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getStatusColor(status) {
  const colors = {
    resolved: '#48bb78',
    active: '#f56565',
    investigating: '#ed8936'
  };
  return colors[status] || '#a0aec0';
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem'
  },
  summaryCard: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  summaryValue: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#2d3748'
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color: '#718096',
    marginTop: '0.25rem'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  filters: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  input: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  lastUpdated: {
    fontSize: '0.75rem',
    color: '#718096'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  timeline: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  timelineItem: {
    display: 'flex',
    gap: '1rem',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '6px',
    transition: 'background 0.15s'
  },
  timelineMarker: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '20px'
  },
  markerDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0
  },
  markerLine: {
    width: '2px',
    flex: 1,
    background: '#e2e8f0',
    marginTop: '4px'
  },
  timelineContent: {
    flex: 1,
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #f7fafc'
  },
  incidentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem'
  },
  incidentType: {
    fontWeight: '600',
    fontSize: '0.875rem'
  },
  statusBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    color: 'white',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  incidentMeta: {
    fontSize: '0.75rem',
    color: '#718096',
    marginBottom: '0.5rem'
  },
  incidentDetails: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.875rem',
    color: '#4a5568'
  },
  incidentDescription: {
    marginTop: '0.5rem',
    fontSize: '0.875rem',
    color: '#718096'
  },
  toolbarActions: {
    display: 'flex',
    gap: '0.5rem'
  },
  exportButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '400px',
    maxWidth: '500px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#4a5568'
  },
  modalInput: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  }
};
